import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

function ClassSchedule() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [classes, setClasses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [streams, setStreams] = useState([]);
  const [sections, setSections] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [schoolTimings, setSchoolTimings] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [schedule, setSchedule] = useState({});
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [holidays, setHolidays] = useState({});
  const [errors, setErrors] = useState({});
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(new Date().getFullYear().toString());
  const { selectedBranchId, handleBranchChange } = useBranchFilter();

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchTeachers();
      fetchSchoolTimings();
      fetchHolidays();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchGrades();
      fetchStreams();
    }
  }, [schoolData?.id, selectedBranchId]);

  useEffect(() => {
    if (schoolData?.id && grades.length >= 0 && streams.length >= 0) {
      fetchClasses();
    }
  }, [schoolData?.id, selectedBranchId, grades, streams]);

  useEffect(() => {
    if (selectedClass) {
      fetchSubjects();
    } else {
      // Clear subjects when no class is selected
      setSubjects([]);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass) {
      fetchSections();
    }
  }, [selectedClass]);

  useEffect(() => {
    // Reset selections when branch changes
    if (selectedBranchId !== undefined) {
      setSelectedClass(null);
      setSelectedSection(null);
      setSchedule({});
      setSubjects([]);
    }
  }, [selectedBranchId]);

  useEffect(() => {
    if (selectedClass && selectedSection) {
      // Refresh holidays before fetching schedule to ensure we have latest data
      fetchHolidays().then(() => {
        fetchSchedule();
      });
    }
  }, [selectedClass, selectedSection, selectedBranchId, selectedTerm, selectedAcademicYear]);

  const fetchGrades = async () => {
    try {
      let query = supabase
        .from('grades')
        .select('*')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('name');

      if (error) {
        console.error('Error fetching grades:', error);
        return;
      }

      setGrades(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchStreams = async () => {
    try {
      let query = supabase
        .from('streams')
        .select('*')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('name');

      if (error) {
        console.error('Error fetching streams:', error);
        return;
      }

      setStreams(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchClasses = async () => {
    try {
      let query = supabase
        .from('classes')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true)
        .not('grade_id', 'is', null); // Only show classes that have grade_id from curriculum

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('grade_id');

      if (error) {
        console.error('Error fetching classes:', error);
        return;
      }

      // Enrich classes with grade and stream names from curriculum
      const enrichedClasses = (data || []).map(cls => {
        const grade = grades.find(g => g.id === cls.grade_id);
        const stream = streams.find(s => s.id === cls.stream_id);
        
        // Debug: Log sections for each class
        console.log(`Class ${cls.class_name || cls.id} - Sections:`, cls.sections, 'Type:', typeof cls.sections);
        
        return {
          ...cls,
          grade: grade || null,
          stream: stream || null,
          // Ensure sections is preserved
          sections: cls.sections || []
        };
      });

      // Deduplicate classes by grade name - keep only one class per grade
      const uniqueClassesMap = new Map();
      enrichedClasses.forEach(cls => {
        const gradeName = cls.grade?.name || (cls.grade_id ? `Grade ${cls.grade_id}` : 'Class');
        if (!uniqueClassesMap.has(gradeName)) {
          uniqueClassesMap.set(gradeName, cls);
        } else {
          // If we already have this grade, merge sections from all classes with the same grade
          const existingClass = uniqueClassesMap.get(gradeName);
          const existingSections = Array.isArray(existingClass.sections) ? existingClass.sections : [];
          const newSections = Array.isArray(cls.sections) ? cls.sections : [];
          const mergedSections = [...new Set([...existingSections, ...newSections])];
          uniqueClassesMap.set(gradeName, {
            ...existingClass,
            sections: mergedSections
          });
        }
      });

      const uniqueClasses = Array.from(uniqueClassesMap.values());

      console.log('All classes with sections:', uniqueClasses.map(c => ({ 
        name: c.class_name, 
        id: c.id, 
        sections: c.sections 
      })));
      
      setClasses(uniqueClasses);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchSections = async () => {
    if (!selectedClass) return;

    try {
      const sectionObjects = [];
      
      // ONLY get sections from the class's sections array (added in /school/classes page)
      // Do NOT use streams/curriculum as fallback
      let classSections = selectedClass.sections || [];
      
      // Debug logging
      console.log('=== FETCHING SECTIONS ===');
      console.log('Selected class:', selectedClass);
      console.log('Class sections (raw):', classSections);
      console.log('Class sections type:', typeof classSections);
      console.log('Is array?', Array.isArray(classSections));
      
      // Handle case where sections might be stored as a string (JSON string)
      if (typeof classSections === 'string' && classSections.trim() !== '') {
        try {
          const parsed = JSON.parse(classSections);
          if (Array.isArray(parsed)) {
            classSections = parsed;
            console.log('Parsed sections from string:', classSections);
          }
        } catch (parseErr) {
          console.warn('Could not parse sections as JSON, treating as single section:', parseErr);
          // If it's a string but not JSON, treat it as a single section
          classSections = [classSections];
        }
      }
      
      if (Array.isArray(classSections) && classSections.length > 0) {
        // Filter out empty sections and create section objects
        classSections
          .filter(section => section && (typeof section === 'string' ? section.trim() !== '' : true))
          .forEach((section, index) => {
            sectionObjects.push({
              id: index + 1,
              section_name: typeof section === 'string' ? section.trim() : String(section),
              class_id: selectedClass.id
            });
          });
      }
      
      console.log('Section objects created:', sectionObjects);
      console.log('=== END FETCHING SECTIONS ===');
      
      // Only show sections from the class's sections array - no fallback to curriculum/streams
      // If no sections found, show empty array (user must add sections in /school/classes first)
      setSections(sectionObjects);
    } catch (err) {
      console.error('Error fetching sections:', err);
      setSections([]);
    }
  };

  const fetchTeachers = async () => {
    try {
      let query = supabase
        .from('teachers')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('is_active', true);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query.order('first_name');

      if (error) {
        console.error('Error fetching teachers:', error);
        return;
      }

      setTeachers(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchSubjects = async () => {
    if (!selectedClass) {
      setSubjects([]);
      return;
    }
    
    // Use the helper function
    fetchSubjectsForClass(selectedClass);
  };

  const fetchSchoolTimings = async () => {
    try {
      let query = supabase
        .from('school_timings')
        .select('*')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching school timings:', error);
        return;
      }

      setSchoolTimings(data || []);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const fetchHolidays = async () => {
    try {
      console.log('=== FETCHING HOLIDAYS ===');
      console.log('School ID:', schoolData.id);
      
      let query = supabase
        .from('schedules')
        .select('holidays')
        .eq('school_id', schoolData.id);

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query.order('created_at', { ascending: false }).limit(1);

      if (error) {
        console.error('Error fetching holidays:', error);
        return;
      }

      console.log('Raw holiday data from database:', data);

      // Set holidays from the most recent schedule, or empty object if none found
      if (data && data.length > 0 && data[0].holidays) {
        setHolidays(data[0].holidays);
        console.log('Loaded holidays:', data[0].holidays);
      } else {
        setHolidays({});
        console.log('No holidays found, using default working days');
      }
      
      console.log('=== END FETCHING HOLIDAYS ===');
    } catch (err) {
      console.error('Error fetching holidays:', err);
      setHolidays({});
    }
  };

  const fetchSchedule = async () => {
    if (!selectedClass || !selectedSection || !selectedTerm || !selectedAcademicYear) return;

    setLoadingSchedule(true);
    try {
      console.log('Loading schedule from Supabase...');
      console.log('Looking for:', {
        school_id: schoolData.id,
        class_id: selectedClass.id,
        section_name: selectedSection.section_name,
        term: selectedTerm,
        academic_year: selectedAcademicYear
      });

      // Load schedule from Supabase
      let query = supabase
        .from('class_schedules')
        .select('*')
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClass.id)
        .eq('section_name', selectedSection.section_name)
        .eq('term', parseInt(selectedTerm))
        .eq('academic_year', parseInt(selectedAcademicYear));

      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      } else {
        query = query.is('branch_id', null);
      }

      const { data, error } = await query
        .order('day')
        .order('period');

      if (error) {
        console.error('Error loading schedule:', error);
        setSchedule({});
        return;
      }

      if (data && data.length > 0) {
        // Convert database entries back to schedule object format
        const scheduleObject = {};
        data.forEach(entry => {
          const key = `${entry.day}_${entry.period}`;
          scheduleObject[key] = {
            subject_name: entry.subject_name,
            teacher_name: entry.teacher_name,
            teacher_id: entry.teacher_id,
            subject_id: entry.subject_id
          };
        });

        setSchedule(scheduleObject);
        console.log('✅ Loaded schedule from Supabase:', scheduleObject);
        console.log('Found', data.length, 'schedule entries');
      } else {
        setSchedule({});
        console.log('No saved schedule found for this class, section, term, and academic year');
      }
    } catch (err) {
      console.error('Error:', err);
      setSchedule({});
    } finally {
      setLoadingSchedule(false);
    }
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  const fetchSubjectsForClass = (classData) => {
    try {
      // Get subjects from the selected class only - ensure we only show subjects that belong to this specific class
      if (classData && classData.subjects && Array.isArray(classData.subjects)) {
        // Filter out empty subjects and get unique subjects for this class
        const classSubjects = classData.subjects
          .filter(subject => subject && subject.trim() !== '')
          .map((subject, index) => ({
            id: index + 1,
            subject_name: subject.trim(),
            school_id: schoolData?.id
          }));

        setSubjects(classSubjects);
        console.log('Loaded subjects for class:', classData.id, 'Subjects:', classSubjects.map(s => s.subject_name));
      } else {
        setSubjects([]);
        console.log('No subjects found for this class. Class data:', classData);
      }
    } catch (err) {
      console.error('Error fetching subjects:', err);
      setSubjects([]);
    }
  };

  const handleClassChange = (classId) => {
    const selectedClassData = classes.find(c => c.id === parseInt(classId));
    
    // Debug: Log the selected class data including sections
    console.log('=== CLASS SELECTED ===');
    console.log('Selected class ID:', classId);
    console.log('Selected class data:', selectedClassData);
    console.log('Sections in selected class:', selectedClassData?.sections);
    console.log('Sections type:', typeof selectedClassData?.sections);
    console.log('Is sections array?', Array.isArray(selectedClassData?.sections));
    console.log('=== END CLASS SELECTED ===');
    
    setSelectedClass(selectedClassData);
    setSelectedSection(null);
    setSchedule({});
    setSubjects([]); // Clear subjects when class changes
    setErrors(prev => ({ ...prev, class: '' }));
    
    // Fetch subjects for the newly selected class
    if (selectedClassData) {
      fetchSubjectsForClass(selectedClassData);
    }
  };

  const handleSectionChange = (sectionId) => {
    // Handle both string and number IDs
    const selectedSectionData = sections.find(s => {
      if (typeof s.id === 'string' && typeof sectionId === 'string') {
        return s.id === sectionId;
      }
      return s.id === parseInt(sectionId) || s.id.toString() === sectionId.toString();
    });
    setSelectedSection(selectedSectionData);
    setErrors(prev => ({ ...prev, section: '' }));
  };

  const handleContinue = () => {
    const newErrors = {};
    if (!selectedClass) {
      newErrors.class = 'Please select a class';
    }
    if (!selectedSection) {
      newErrors.section = 'Please select a section';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      // Both selected, can proceed to schedule grid
      // The schedule grid will show automatically when both are selected
    }
  };

  const handleCancel = () => {
    setSelectedClass(null);
    setSelectedSection(null);
    setSchedule({});
    setErrors({});
  };

  const handleScheduleChange = (day, period, subjectName) => {
    const key = `${day}_${period}`;
    
    // Find the teacher assigned to this subject for this class
    const assignedTeacher = findTeacherForSubject(subjectName);
    
    // If no specific teacher found, assign the first available teacher as fallback
    let teacherName = '';
    let teacherId = null;
    
    if (assignedTeacher) {
      teacherName = `${assignedTeacher.first_name} ${assignedTeacher.last_name}`;
      teacherId = assignedTeacher.id;
    } else if (teachers.length > 0 && subjectName) {
      // Fallback: assign the first teacher
      const fallbackTeacher = teachers[0];
      teacherName = `${fallbackTeacher.first_name} ${fallbackTeacher.last_name}`;
      teacherId = fallbackTeacher.id;
      console.log('Using fallback teacher:', teacherName);
    }
    
    setSchedule(prev => ({
      ...prev,
      [key]: {
        subject_name: subjectName,
        teacher_name: teacherName,
        teacher_id: teacherId
      }
    }));
  };

  const findTeacherForSubject = (subjectName) => {
    if (!selectedClass || !subjectName) return null;
    
    console.log('Finding teacher for subject:', subjectName);
    console.log('Selected class:', selectedClass);
    console.log('Available teachers:', teachers);
    
    // Check if the class has subjects and teachers arrays
    if (selectedClass.subjects && selectedClass.teachers) {
      const subjectIndex = selectedClass.subjects.findIndex(subject => subject === subjectName);
      console.log('Subject index:', subjectIndex);
      
      if (subjectIndex !== -1 && selectedClass.teachers[subjectIndex]) {
        const teacherId = selectedClass.teachers[subjectIndex];
        console.log('Teacher ID from class:', teacherId);
        const teacher = teachers.find(teacher => teacher.id === parseInt(teacherId));
        console.log('Found teacher:', teacher);
        return teacher;
      }
    }
    
    // Fallback: try to find a teacher who teaches this subject
    console.log('Trying fallback teacher search...');
    const fallbackTeacher = teachers.find(teacher => {
      if (teacher.subjects && Array.isArray(teacher.subjects)) {
        return teacher.subjects.includes(subjectName);
      }
      return false;
    });
    
    console.log('Fallback teacher found:', fallbackTeacher);
    return fallbackTeacher;
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';

    let time = timeString.toString().trim();

    if (time.match(/^\d{1,2}$/)) {
      time = time.padStart(2, '0') + ':00';
    } else if (time.match(/^\d{1,2}:\d{1,2}$/)) {
      const parts = time.split(':');
      time = `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    } else if (time.match(/^\d{1,2}:\d{2}:\d{2}$/)) {
      time = time.substring(0, 5);
    } else if (time.match(/^\d{4}$/)) {
      time = `${time.substring(0, 2)}:${time.substring(2, 4)}`;
    }

    return time;
  };

  const addMinutes = (timeString, minutes) => {
    if (!timeString || timeString === 'N/A') return 'N/A';

    const [hours, mins] = formatTime(timeString).split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMins = totalMinutes % 60;

    return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
  };

  const getPeriodTimeLabel = (periodNumber) => {
    const latestTiming = schoolTimings[0];

    if (!latestTiming?.start_time) {
      return `Period ${periodNumber}`;
    }

    const periodDuration = latestTiming.period_time || 40;
    const breaks = latestTiming.breaks || [];
    let periodStartTime = formatTime(latestTiming.start_time);

    for (let i = 1; i < periodNumber; i++) {
      const periodEndTime = addMinutes(periodStartTime, periodDuration);
      const breakAfter = breaks.find(breakItem => {
        const breakStart = formatTime(breakItem.startTime || breakItem.start_time);
        return breakStart === periodEndTime;
      });

      periodStartTime = breakAfter
        ? formatTime(breakAfter.endTime || breakAfter.end_time)
        : periodEndTime;
    }

    return `${periodStartTime} - ${addMinutes(periodStartTime, periodDuration)}`;
  };

  const buildScheduleEntries = () => {
    const scheduleEntries = [];

    Object.keys(schedule).forEach(key => {
      const [day, period] = key.split('_');
      const scheduleItem = schedule[key];

      if (scheduleItem.subject_name) {
        scheduleEntries.push({
          school_id: schoolData.id,
          branch_id: selectedBranchId || null,
          class_id: selectedClass.id,
          section_name: selectedSection.section_name,
          day,
          period: parseInt(period),
          subject_name: scheduleItem.subject_name,
          teacher_name: scheduleItem.teacher_name || 'TBA',
          teacher_id: scheduleItem.teacher_id || null,
          subject_id: scheduleItem.subject_id || null,
          term: parseInt(selectedTerm),
          academic_year: parseInt(selectedAcademicYear)
        });
      }
    });

    return scheduleEntries;
  };

  const validateTeacherConflicts = async (scheduleEntries) => {
    const entriesWithTeachers = scheduleEntries.filter(entry => entry.teacher_id);

    if (entriesWithTeachers.length === 0) {
      return true;
    }

    const teacherIds = [...new Set(entriesWithTeachers.map(entry => entry.teacher_id))];
    const days = [...new Set(entriesWithTeachers.map(entry => entry.day))];
    const periods = [...new Set(entriesWithTeachers.map(entry => entry.period))];

    let query = supabase
      .from('class_schedules')
      .select('id, class_id, section_name, day, period, teacher_id, teacher_name, subject_name')
      .eq('school_id', schoolData.id)
      .eq('term', parseInt(selectedTerm))
      .eq('academic_year', parseInt(selectedAcademicYear))
      .in('teacher_id', teacherIds)
      .in('day', days)
      .in('period', periods);

    if (selectedBranchId) {
      query = query.eq('branch_id', selectedBranchId);
    } else {
      query = query.is('branch_id', null);
    }

    const { data: existingSchedules, error } = await query;

    if (error) {
      console.error('Error checking teacher conflicts:', error);
      alert('Failed to check teacher availability. Please try again.');
      return false;
    }

    const conflicts = [];

    entriesWithTeachers.forEach(entry => {
      const matchingConflict = (existingSchedules || []).find(existing => {
        const isSameSlot =
          parseInt(existing.teacher_id) === parseInt(entry.teacher_id) &&
          existing.day === entry.day &&
          parseInt(existing.period) === parseInt(entry.period);

        const isCurrentSchedule =
          parseInt(existing.class_id) === parseInt(selectedClass.id) &&
          existing.section_name === selectedSection.section_name;

        return isSameSlot && !isCurrentSchedule;
      });

      if (matchingConflict) {
        conflicts.push({
          newEntry: entry,
          existing: matchingConflict
        });
      }
    });

    if (conflicts.length === 0) {
      return true;
    }

    const conflictClassIds = [...new Set(conflicts.map(conflict => conflict.existing.class_id))];
    const { data: conflictClasses } = await supabase
      .from('classes')
      .select('id, class_name')
      .in('id', conflictClassIds);

    const classNameById = (conflictClasses || []).reduce((acc, cls) => {
      acc[cls.id] = cls.class_name;
      return acc;
    }, {});

    const conflict = conflicts[0];
    const existingClassName = classNameById[conflict.existing.class_id] || `Class ID ${conflict.existing.class_id}`;
    const dayName = getDayName(conflict.newEntry.day);
    const periodTime = getPeriodTimeLabel(conflict.newEntry.period);

    alert(
      `${conflict.newEntry.teacher_name} is already assigned at this time.\n\n` +
      `Time: ${dayName}, ${periodTime}\n` +
      `Existing class: ${existingClassName} - ${conflict.existing.section_name}\n` +
      `Existing subject: ${conflict.existing.subject_name}\n\n` +
      'Please choose a different period or change the teacher for one of the classes.'
    );

    return false;
  };

  const saveSchedule = async () => {
    if (!selectedClass || !selectedSection) {
      alert('Please select a class and section first');
      return;
    }
    
    if (!selectedTerm || !selectedAcademicYear) {
      alert('Please select both Term and Academic Year before saving');
      return;
    }

    try {
      console.log('Starting save process...');
      console.log('Selected class:', selectedClass);
      console.log('Selected section:', selectedSection);
      console.log('Schedule object:', schedule);
      console.log('School data:', schoolData);

      // Prepare schedule data for database insertion before deleting existing rows.
      const scheduleEntries = buildScheduleEntries();

      console.log('Schedule entries prepared:', scheduleEntries);
      console.log('=== DEBUGGING SAVE DATA ===');
      console.log('Saving with:', {
        school_id: schoolData.id,
        class_id: selectedClass.id,
        section_name: selectedSection.section_name,
        sample_entry: scheduleEntries[0]
      });
      console.log('=== END DEBUGGING SAVE ===');

      if (scheduleEntries.length === 0) {
        console.log('No schedule entries to insert - schedule object is empty');
        alert('No schedule data found. Please add subjects to the schedule first.');
        return;
      }

      const canSaveSchedule = await validateTeacherConflicts(scheduleEntries);
      if (!canSaveSchedule) {
        return;
      }

      // First, delete existing schedule for this class, section, term, and academic year
      console.log('Deleting existing schedule...');
      let deleteQuery = supabase
        .from('class_schedules')
        .delete()
        .eq('school_id', schoolData.id)
        .eq('class_id', selectedClass.id)
        .eq('section_name', selectedSection.section_name)
        .eq('term', parseInt(selectedTerm))
        .eq('academic_year', parseInt(selectedAcademicYear));

      if (selectedBranchId) {
        deleteQuery = deleteQuery.eq('branch_id', selectedBranchId);
      } else {
        deleteQuery = deleteQuery.is('branch_id', null);
      }

      const { error: deleteError } = await deleteQuery;

      if (deleteError) {
        console.error('Error deleting existing schedule:', deleteError);
        alert('Failed to clear existing schedule. Please try again.');
        return;
      }

      console.log('Existing schedule deleted successfully');

      // Insert new schedule entries
      if (scheduleEntries.length > 0) {
        console.log('Inserting schedule entries to database...');
        const { data, error } = await supabase
          .from('class_schedules')
          .insert(scheduleEntries)
          .select();

        if (error) {
          console.error('Error saving schedule to database:', error);
          alert(`Failed to save schedule to database: ${error.message}`);
          return;
        }

        console.log('Schedule saved to database successfully:', data);
        
        // Show beautiful success notification
        setShowSuccessNotification(true);
        setTimeout(() => {
          setShowSuccessNotification(false);
        }, 3000);
        console.log('Schedule saved to database:', data);
      } else {
        console.log('No schedule entries to insert - schedule object is empty');
        alert('No schedule data found. Please add subjects to the schedule first.');
        return;
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to save schedule. Please try again.');
    }
  };

  const getWorkingDays = () => {
    // Always use all 7 days as potential working days, then filter out holidays
    const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    
    // Filter out holiday days
    // holidays[day] === true means it's a holiday, so we exclude it
    // holidays[day] === false or undefined means it's a working day, so we include it
    const filteredDays = allDays.filter(day => {
      // If holidays object exists and the day is marked as true (holiday), exclude it
      if (holidays && typeof holidays[day] === 'boolean') {
        return !holidays[day]; // Return true if NOT a holiday (false means working day)
      }
      // If no holiday data, include all days
      return true;
    });
    
    return filteredDays;
  };

  const getPeriods = () => {
    if (schoolTimings.length === 0) return Array.from({ length: 8 }, (_, i) => i + 1);
    
    // Get the most recent timing configuration
    const latestTiming = schoolTimings[0];
    return Array.from({ length: latestTiming.number_of_periods || 8 }, (_, i) => i + 1);
  };

  const getDayName = (day) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳</div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">❌</div>
        <p>School data not found. Please login again.</p>
        <button onClick={() => navigate('/schoollogin')} className="submit-btn">
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content nexy-class-schedule-page">
        <div className="schedule-content">
          {/* Page Title Section */}
          <div className="nexy-schedule-title-card">
            <div className="nexy-calendar-icon">📅</div>
            <h1>Class Schedule</h1>
            <p>Create and manage class schedules based on school timings</p>
          </div>

          {/* Branch Selector Section */}
          <div className="dashboard-branch-row schedule-view-branch-row" style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
            <BranchSelector
              schoolId={schoolData?.id}
              selectedBranchId={selectedBranchId}
              onBranchChange={handleBranchChange}
            />
            <div className="branch-context" style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#6B7280' }}>
              {selectedBranchId 
                ? `📍 Showing class schedules for the selected branch. Working days are based on branch holidays.` 
                : '⚠️ Please select a branch to view and manage class schedules'}
            </div>
          </div>

          {/* Show message if no branch is selected */}
          {!selectedBranchId ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem 2rem', 
              background: '#F9FAFB', 
              borderRadius: '8px', 
              border: '1px solid #E5E7EB',
              marginBottom: '2rem'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📍</div>
              <h3 style={{ marginBottom: '1rem', color: '#374151' }}>Select a Branch</h3>
              <p style={{ color: '#6B7280', fontSize: '1.1rem', lineHeight: '1.6' }}>
                Please select a branch from the dropdown above to view and manage class schedules for that branch. Working days will be automatically filtered based on the branch's holiday schedule.
              </p>
            </div>
          ) : (
            <>
          {/* Class and Section Selection */}
          <div className="nexy-selection-section">
            <div className="nexy-selection-card">
              <div className="nexy-section-header">
                <div className="nexy-books-icon">📚</div>
                <h2>Select Class and Section</h2>
              </div>
              
              <div className="nexy-dropdown-container">
                <div className="nexy-form-group">
                  <label htmlFor="classSelect">
                    Select Class <span className="nexy-asterisk">*</span>
                  </label>
                  <select
                    id="classSelect"
                    value={selectedClass?.id || ''}
                    onChange={(e) => handleClassChange(e.target.value)}
                    className={`nexy-dropdown ${errors.class ? 'nexy-error' : ''} ${selectedClass ? 'nexy-selected' : ''}`}
                  >
                    <option value="">Choose a class...</option>
                    {classes.map((cls) => {
                      const gradeName = cls.grade?.name || (cls.grade_id ? `Grade ${cls.grade_id}` : 'Class');
                      // Show only class name (grade name) without stream/section name
                      return (
                        <option key={cls.id} value={cls.id}>
                          {gradeName}
                        </option>
                      );
                    })}
                  </select>
                  {errors.class && <span className="nexy-error-message">{errors.class}</span>}
                  {selectedClass && !errors.class && <span className="nexy-checkmark">✓</span>}
                </div>

                <div className="nexy-form-group">
                  <label htmlFor="sectionSelect">
                    Select Section <span className="nexy-asterisk">*</span>
                  </label>
                  <select
                    id="sectionSelect"
                    value={selectedSection?.id?.toString() || ''}
                    onChange={(e) => handleSectionChange(e.target.value)}
                    className={`nexy-dropdown ${errors.section ? 'nexy-error' : ''} ${selectedSection ? 'nexy-selected' : ''}`}
                    disabled={!selectedClass}
                  >
                    <option value="">Choose a section...</option>
                    {sections.length > 0 ? (
                      sections.map((section) => (
                        <option key={section.id} value={section.id.toString()}>
                          {section.section_name}
                        </option>
                      ))
                    ) : (
                      <option disabled>No sections available. Please add sections in Classes page first.</option>
                    )}
                  </select>
                  {errors.section && <span className="nexy-error-message">{errors.section}</span>}
                  {selectedSection && !errors.section && <span className="nexy-checkmark">✓</span>}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="nexy-action-buttons">
                <button
                  type="button"
                  className="nexy-cancel-btn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="nexy-continue-btn"
                  onClick={handleContinue}
                  disabled={!selectedClass || !selectedSection}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>

          {/* Schedule Grid */}
          {selectedClass && selectedSection && (
            <div className="schedule-grid-section">
              <div className="schedule-grid-card">
                <div className="schedule-grid-header">
                  <div className="schedule-header-left">
                    <h2>📋 Schedule for {(() => {
                      const gradeName = selectedClass.grade?.name || (selectedClass.grade_id ? `Grade ${selectedClass.grade_id}` : 'Class');
                      // Show only class name (grade name) without stream name
                      return gradeName;
                    })()} - {selectedSection.section_name}</h2>
                    <div className="term-year-selection-container" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '150px' }}>
                        <label htmlFor="termSelect" className="semester-label">Term:</label>
                        <select
                          id="termSelect"
                          value={selectedTerm}
                          onChange={(e) => {
                            setSelectedTerm(e.target.value);
                            setSchedule({});
                          }}
                          className="semester-select"
                        >
                          <option value="">Select Term</option>
                          <option value="1">Term 1</option>
                          <option value="2">Term 2</option>
                          <option value="3">Term 3</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '150px' }}>
                        <label htmlFor="academicYearSelect" className="semester-label">Academic Year:</label>
                        <select
                          id="academicYearSelect"
                          value={selectedAcademicYear}
                          onChange={(e) => {
                            setSelectedAcademicYear(e.target.value);
                            setSchedule({});
                          }}
                          className="semester-select"
                        >
                          {(() => {
                            const currentYear = new Date().getFullYear();
                            const years = [];
                            // Show current year and 2 years before and after
                            for (let i = currentYear - 2; i <= currentYear + 2; i++) {
                              years.push(i);
                            }
                            return years.map(year => (
                              <option key={year} value={year.toString()}>{year}</option>
                            ));
                          })()}
                        </select>
                      </div>
                    </div>
                  </div>
                  <button 
                    className="save-schedule-btn"
                    onClick={saveSchedule}
                    disabled={loadingSchedule}
                  >
                    {loadingSchedule ? 'Saving...' : '💾 Save Schedule'}
                  </button>
                </div>

                {loadingSchedule ? (
                  <div className="loading-container">
                    <div className="loading-spinner">⏳</div>
                    <p>Loading schedule...</p>
                  </div>
                ) : (
                  <div className="schedule-table-container">
                    <table className="schedule-table">
                      <thead>
                        <tr className="schedule-table-header">
                          <th className="period-header">Period</th>
                          {getWorkingDays().map(day => (
                            <th key={day} className="day-header">
                              {getDayName(day)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {getPeriods().map(period => (
                          <tr key={period} className="schedule-table-row">
                            <td className="period-cell">
                              <div className="period-label">Period {period}</div>
                            </td>
                            {getWorkingDays().map(day => {
                              const key = `${day}_${period}`;
                              const scheduleItem = schedule[key] || {};
                              
                              return (
                                <td key={day} className="schedule-cell">
                                  <div className="schedule-assignment">
                                    <select
                                      value={scheduleItem.subject_name || ''}
                                      onChange={(e) => handleScheduleChange(day, period, e.target.value)}
                                      className="schedule-select"
                                    >
                                      <option value="">Select Subject</option>
                                      {subjects.map(subject => (
                                        <option key={subject.id} value={subject.subject_name}>
                                          {subject.subject_name}
                                        </option>
                                      ))}
                                    </select>
                                    
                                    {scheduleItem.teacher_name && (
                                      <div className="assigned-teacher">
                                        <span className="teacher-label">Teacher:</span>
                                        <span className="teacher-name">{scheduleItem.teacher_name}</span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Instructions */}
          {!selectedClass && (
            <div className="nexy-instructions-section">
              <div className="nexy-instructions-card">
                <div className="nexy-instructions-header">
                  <div className="nexy-pencil-icon">📝</div>
                  <h2>How to Create a Schedule</h2>
                </div>
                <div className="nexy-steps-container">
                  <div className="nexy-step">
                    <span className="nexy-step-number">1.</span>
                    <span className="nexy-step-text">Select a class from the dropdown above</span>
                  </div>
                  <div className="nexy-step">
                    <span className="nexy-step-number">2.</span>
                    <span className="nexy-step-text">Choose a section for that class</span>
                  </div>
                  <div className="nexy-step">
                    <span className="nexy-step-number">3.</span>
                    <span className="nexy-step-text">Assign subjects and teachers to each period for each day</span>
                  </div>
                  <div className="nexy-step">
                    <span className="nexy-step-number">4.</span>
                    <span className="nexy-step-text">Click "Save Schedule" to save your changes</span>
                  </div>
                </div>
                <div className="nexy-note-section">
                  <span className="nexy-note-label">Note:</span>
                  <span className="nexy-note-text"> The schedule grid is based on your school timing configuration. Make sure you have set up school timings first.</span>
                </div>
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </div>

      {/* Success Notification Popup */}
      {showSuccessNotification && (
        <div className="schedule-success-notification">
          <div className="schedule-success-content">
            <div className="schedule-success-icon">✅</div>
            <div className="schedule-success-text">
              <h3>Schedule Saved Successfully!</h3>
              <p>Your class schedule has been saved to the database.</p>
            </div>
            <button 
              className="schedule-success-close"
              onClick={() => setShowSuccessNotification(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default ClassSchedule;
